// src/App.tsx
// src/App.tsx
// src/App.tsx
// src/App.tsx
// src/App.tsx
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import { auth, onAuthStateChanged } from './firebase/config';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useUser } from './contexts/UserContext';
import { useWebSocketChat } from './contexts/WebSocketChatContext';

// Redux imports
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';

// Components
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import MapView from './pages/MapView';
import UploadPhotos from './pages/UploadPhotos';
import PhotoDetails from './pages/PhotoDetails';
import TimelineView from './pages/Timeline/index';
import PhotoManagement from './pages/PhotoManagement';
import TripManagement from './pages/TripManagement';
import TripPlanner from './pages/TripPlanner/TripPlanner';
import ItineraryBuilder from './pages/ItineraryBuilder';
import ActivitiesBuilder from './pages/ActivitiesBuilder/index';
import UserManagement from './pages/UserManagement'; // New import for user management
import Login from './pages/Auth/Login'; // New import for login
import Register from './pages/Auth/Register'; // New import for registration
import Profile from './pages/Profile/index'; // New import for user profile
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import FinancialDashboard from './pages/SuperAdmin/FinancialDashboard';
import AcceptInvite from './pages/AcceptInvite';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PhoneSignup from './pages/Auth/PhoneSignup';
import TripsPage from './pages/TripsPage';
import QRCodeShare from './pages/QRCodeShare';
import TripDetailPage from './pages/TripDetailPage';
import AudioTest from './components/AudioTest'; // Import AudioTest component
import InvitationManagement from './pages/InvitationManagement'; // Import InvitationManagement page
import ChatPage from './pages/Chat'; // Import Chat page
import Inbox from './pages/Inbox';

// Events pages
import EventsPage from './pages/Events';
import CreateEventPage from './pages/Events/CreateEvent';
import EditEvent from './pages/Events/EditEvent';
import EventDetailPage from './pages/Events/EventDetail'; // Fix import error
import PublicEventPage from './pages/Events/PublicEventPage';
import TicketConfirmation from './pages/Events/TicketConfirmation'; // Fix import error
import PublicSubscription from './pages/PulicSubscription'; // Import for public subscription page
import PublicLandingPage from './pages/PublicLandingPage'; // Import for public user landing page
import CanvaCallback from './pages/CanvaCallback'; // Fix import error
import AssetGallery from './pages/Organizations/AssetGallery'; // Fix import error
import EventEmbedView from './pages/Events/EventEmbedView'; // Import EventEmbedView
import AIPhotoStudioPage from './pages/PersonalAccount/AIPhotoStudio/AIPhotoStudioPage'; // Import AIPhotoStudioPage

// Import the Calendar page
import CalendarPage from './pages/Calendar';

// Add People page import
import PeoplePage from './pages/People';
import ScheduledCallsPage from './pages/ScheduledCalls';
import ScheduledCallPublic from './pages/ScheduledCallPublic';

// Organization pages
import OrganizationsList from './pages/Organizations/OrganizationsList';
import CreateOrganizationPage from './pages/Organizations/CreateOrganization';
import OrganizationProfile from './pages/Organizations/OrganizationProfile';
import OrganizationDashboard from './pages/Organizations/OrganizationDashboard';
import OrganizationMapView from './pages/Organizations/OrganizationMapView';
import OrganizationTimelineView from './pages/Organizations/OrganizationTimelineView';
import OrganizationCalendar from './pages/Organizations/OrganizationCalendar';
import OrganizationEvents from './pages/Organizations/OrganizationEvents';
import OrganizationTripsPage from './pages/Organizations/OrganizationTripsPage';
import OrganizationPeoplePage from './pages/Organizations/OrganizationPeoplePage';
import OrganizationAttendees from './pages/Organizations/OrganizationAttendees';
import OrganizationChat from './pages/Organizations/OrganizationChat';
import OrganizationUploadPhotos from './pages/Organizations/OrganizationUploadPhotos';
import OrganizationContacts from './pages/Organizations/OrganizationContacts'; // Import for Contacts
import CreateOrgEvent from './pages/Organizations/OrganizationEvents/CreateEvent';
import OrganizationEventDetail from './pages/Organizations/OrganizationEvents/EventDetail';
import EditOrgEvent from './pages/Organizations/OrganizationEvents/EditOrgEvent';
import OrganizationSubscriptions from './pages/Organizations/OrganizationSubscriptions'; // Import for Subscriptions
import PrivacyPage from './pages/Privacy';
import TermsPage from './pages/Terms';
import ContactPage from './pages/Contact';
import HelpPage from './pages/Help';
import SuperAdminPage from './pages/SuperAdmin';
import AcceptInvitationPage from './components/invitations/AcceptInvitationPage';
import AcceptAlbumInvitePage from './components/albums/AcceptAlbumInvitePage';
import AcceptNetworkInvite from './pages/AcceptNetworkInvite';
import ContactDetail from './pages/Organizations/OrganizationContacts/components/ContactDetail'; // Updated import path
import ContactCreate from './pages/Organizations/OrganizationContacts/components/ContactCreate'; // Import for creating contacts
import OrganizationMarketingStudio from './pages/Organizations/Marketing/OrganizationMarketingStudio'; // Import MarketingStudio from correct path
import OrganizationAdminDashboard from './pages/Organizations/OrganizationAdminDashboard'; // Import OrganizationAdminDashboard
import EventManagement from './pages/SuperAdmin/EventManagement'; // Import Event Management component
import Welcome from './pages/Welcome';
import SharedCollections from './pages/SharedCollections';
import PersonalPhotos from './pages/PersonalPhotos';
import PublicSharedCollection from './pages/PublicSharedCollection';
import SocialSharedCollection from './pages/SocialSharedCollection';
import Ambassador from './pages/Ambassador';
import About from './pages/About';
import Features from './pages/Features';
import SubscriberPage from './pages/SubscriberPage';
import SubscriptionPurchasePage from './pages/SubscriptionPurchasePage'; // Correct import path
import ExploreSubscriptionsPage from './pages/ExploreSubscriptionsPage'; // Import the new page
import MyTicketsPage from './pages/MyTicketsPage'; // Import the MyTicketsPage
import MyBookingsPage from './pages/MyBookingsPage'; // Import for bookings page
import BookingDetailPage from './pages/BookingDetailPage'; // Import for booking details page
import ExploreEventsPage from './pages/ExploreEventsPage'; // Import the ExploreEventsPage
import TicketPurchasePage from './pages/TicketPurchasePage'; // Import the TicketPurchasePage
import EventDetailsPage from './pages/EventDetailsPage'; // Import the EventDetailsPage
import CreateVenuePage from './pages/Venues/CreateVenue'; // Import venue creation page
import VenuesList from './pages/Venues/VenuesList'; // Import venues list page
import VenueDetail from './pages/Venues/VenueDetail'; // Import venue detail page
import VenueSignupFlow from './pages/Auth/VenueSignup/VenueSignupFlow'; // Import the venue signup flow component
import CreatePackage from './pages/Venues/CreatePackage'; // Import package creation page
import PackageDetail from './pages/Venues/PackageDetail'; // Import package detail page
import EditPackage from './pages/Venues/EditPackage'; // Import package edit page
import VenueBookingsPage from './pages/Venues/VenueBookingsPage'; // Import venue bookings page
import VenueDashboard from './pages/Venues/VenueDashboard'; // Import venue dashboard page
import ExplorePackages from './pages/Public/ExplorePackages'; // Import public packages page
import PublicPackageDetail from './pages/Public/PublicPackageDetail'; // Import public package detail page
// Context Providers
import { UserProvider } from './contexts/UserContext';
import { TripProvider } from './contexts/TripContext';
import { AuthProvider } from './contexts/AuthContext'; // Import AuthProvider
import { WebSocketChatProvider } from './contexts/WebSocketChatContext'; // Import WebSocketChatProvider
import AuthGuard from './components/AuthGuard'; // New component for route protection
import { NotificationProvider } from './contexts/NotificationContext';
import { EventProvider } from './contexts/EventContext'; // Import EventProvider
import { OrganizationProvider } from './contexts/OrganizationContext'; // Import OrganizationProvider
import { MarketingStudioProvider } from './contexts/MarketingStudioContext';
import { VenueProvider } from './contexts/VenueContext'; // Import VenueProvider
// Import MarketingStudioProvider
import PersonalAccountSubscription from './pages/personalSubscription/PersonalAccountSubscription';
import CancelConfirmation from './pages/personalSubscription/CancelConfirmation';
import SuccessSubscription from './pages/personalSubscription/SuccessSubscription';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordEmail from './pages/ResetPasswordEmail';
import PublicSubscriptionPage from './pages/OrgPublicSubscription';
// Import the ResponsiveProvider
import { ResponsiveProvider } from './utils/responsive';
import { VideoCallProvider } from './contexts/VideoCallContext';
import VideoCallOverlay from './components/VirtualEvent/VideoCallOverlay';
import JoinRoomPage from './components/VirtualEvent/JoinRoomPage';
import { ScheduledCallVideoProvider } from './contexts/ScheduledCallVideoContext';
import ScheduledCallVideoOverlay from './components/ScheduledCall/ScheduledCallVideoOverlay';
import ScheduledCallRoomPage from './pages/ScheduledCallRoom';
import AlbumsListPage from './pages/AlbumsListPage';
import AlbumDetailPage from './pages/AlbumDetailPage';
import PublicAlbumViewer from './components/albums/PublicAlbumViewer';
import LandingPage from './pages/LandingPage';
import Feed from './pages/Feed';
import webSocketService from './services/WebSocketService';
import { FloatingChatProvider } from './contexts/FloatingChatContext';
import GlobalFloatingChat from './components/GlobalFloatingChat/GlobalFloatingChat';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const PublicAlbumPage = lazy(() => import('./pages/albums/PublicAlbumPage'));

// Using our custom theme with gradient colors

// Auth route wrapper component
interface PrivateRouteProps {
   children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
   const [user, setUser] = useState<any>(null);
   const [loading, setLoading] = useState(true);

   const { currentUser } = useUser();

   useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
         setUser(user);
         setLoading(false);
      });

      return () => unsubscribe();
   }, []);

   // PrivateRoute only handles authentication
   // All subscription logic is handled at the component level

   // Show loading while checking authentication
   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               justifyContent: 'center',
               alignItems: 'center',
               height: '100vh',
            }}
         >
            <CircularProgress />
            <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
               Authenticating...
            </Typography>
         </Box>
      );
   }

   if (!user) {
      return <Navigate to='/' state={{ from: { pathname: window.location.pathname } }} replace />;
   }

   return <>{children}</>;
};

// Layout wrapper component for protected routes
const ProtectedLayout = () => {
   return (
      <PrivateRoute>
         <NotificationProvider>
            <Layout>
               <Outlet />
            </Layout>
            <GlobalFloatingChat />
         </NotificationProvider>
      </PrivateRoute>
   );
};

// Main App component that uses useUser hook
const AppContent = () => {
   const { currentUser, isLoading } = useUser();

   useEffect(() => {
      if (!currentUser || !webSocketService) return;

      // Initialize WebSocket connection for personal notification room
      const initializeWebSocket = async () => {
         try {
            await webSocketService.connect(
               currentUser.uid,
               currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
            );
         } catch (error) {
            console.error('❌ Failed to connect WebSocket for notifications:', error);
         }
      };
      initializeWebSocket();
   }, [currentUser]);

   if (isLoading) {
      return (
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               height: '100vh',
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   return (
      <MarketingStudioProvider>
         <Router>
            <VideoCallProvider>
               <ScheduledCallVideoProvider>
                  <VideoCallOverlay />
                  <ScheduledCallVideoOverlay />
                  <Routes>
                     {/* Unprotected routes - without Layout */}
                     <Route path='/' element={<LandingPage />} />
                     <Route path='/login' element={<LoginPage />} />
                     <Route path='/signup' element={<SignupPage />} />
                     <Route path='/phone-signup' element={<PhoneSignup />} />
                     <Route path='/signup/venue' element={<VenueSignupFlow />} />
                     <Route path='/accept-invitation' element={<AcceptInvitationPage />} />
                     <Route path='/accept-album-invitation' element={<AcceptAlbumInvitePage />} />
                     <Route
                        path='/accept-network-invite/:requestId'
                        element={<AcceptNetworkInvite />}
                     />
                     <Route path='/accept-network-invitation' element={<AcceptNetworkInvite />} />
                     <Route path='/privacy' element={<PrivacyPage />} />
                     <Route path='/terms' element={<TermsPage />} />
                     <Route path='/contact' element={<ContactPage />} />
                     <Route path='/help' element={<HelpPage />} />
                     <Route path='/events/public/:eventId' element={<PublicEventPage />} />
                     <Route path='/events/embed/:publicLinkId' element={<EventEmbedView />} />
                     <Route
                        path='/albums/public/:albumId'
                        element={
                           <Suspense
                              fallback={
                                 <Box
                                    sx={{
                                       minHeight: '100vh',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       bgcolor: '#000',
                                    }}
                                 >
                                    <CircularProgress size={60} sx={{ color: 'white' }} />
                                 </Box>
                              }
                           >
                              <PublicAlbumPage />
                           </Suspense>
                        }
                     />
                     <Route path='/:userId/:username' element={<PublicLandingPage />} />
                     <Route path='/qr/:userId' element={<QRCodeShare />} />
                     <Route path='/profile/public/:userId' element={<QRCodeShare />} />
                     <Route path='/subscribe/:orgId' element={<PublicSubscriptionPage />} />
                     <Route path='/public-subscription' element={<PublicSubscription />} />
                     <Route path='/canva-callback' element={<CanvaCallback />} />
                     <Route path='/reset-password-email' element={<ResetPasswordEmail />} />
                     <Route path='/reset-password' element={<ResetPassword />} />
                     <Route path='/welcome' element={<Welcome />} />
                     <Route path='/shared/album/:shareToken' element={<PublicAlbumViewer />} />
                     <Route
                        path='/shared/collection/:shareToken'
                        element={<PublicSharedCollection />}
                     />
                     <Route
                        path='/social/collection/:shareToken'
                        element={<SocialSharedCollection />}
                     />
                     <Route path='/ambassador' element={<Ambassador />} />
                     <Route path='/about' element={<About />} />
                     <Route path='/features' element={<Features />} />
                     <Route path='/scheduled-call/:callId' element={<ScheduledCallPublic />} />

                     {/* Protected routes - with Layout wrapper */}
                     <Route element={<ProtectedLayout />}>
                        <Route path='/feed' element={<Feed />} />
                        <Route path='/dashboard' element={<Dashboard />} />
                        <Route path='/join-room/:roomId' element={<JoinRoomPage />} />
                        <Route path='/subscription' element={<PersonalAccountSubscription />} />
                        <Route path='/cancel-confirmation' element={<CancelConfirmation />} />
                        <Route path='/success-subscription' element={<SuccessSubscription />} />

                        <Route path='/map' element={<MapView />} />
                        <Route path='/upload' element={<UploadPhotos />} />
                        <Route path='/photo/:id' element={<PhotoDetails />} />
                        <Route path='/timeline' element={<TimelineView />} />
                        <Route path='/manage' element={<PhotoManagement />} />
                        <Route path='/trips' element={<TripsPage />} />
                        <Route path='/trip/:tripId' element={<TripDetailPage />} />
                        <Route path='/trip-planner' element={<TripPlanner />} />
                        <Route path='/itinerary' element={<ItineraryBuilder />} />
                        <Route path='/itinerary/:tripId' element={<ItineraryBuilder />} />
                        <Route path='/activities' element={<ActivitiesBuilder />} />
                        <Route path='/activities/:tripId' element={<ActivitiesBuilder />} />
                        <Route path='/profile' element={<Profile />} />
                        <Route path='/inbox' element={<Inbox />} />
                        <Route path='/chat' element={<ChatPage />} />
                        <Route path='/users' element={<UserManagement />} />
                        <Route
                           path='/users/invite'
                           element={<UserManagement showInviteDialog={true} />}
                        />
                        <Route path='/users/:userId' element={<UserManagement />} />
                        <Route path='/admin/dashboard' element={<SuperAdminDashboard />} />
                        <Route path='/admin/financial' element={<FinancialDashboard />} />
                        <Route path='/trip-sharing/:tripId' element={<TripManagement />} />
                        <Route path='/audio-test' element={<AudioTest />} />
                        <Route path='/invitations' element={<InvitationManagement />} />
                        <Route path='/events' element={<EventsPage />} />
                        <Route path='/events/create' element={<CreateEventPage />} />
                        <Route path='/events/:eventId' element={<EventDetailPage />} />
                        <Route path='/events/:eventId/edit' element={<EditEvent />} />
                        <Route path='/calendar' element={<CalendarPage />} />
                        <Route path='/people' element={<PeoplePage />} />
                        <Route path='/scheduled-calls' element={<ScheduledCallsPage />} />
                        <Route
                           path='/scheduled-call-room/:roomId'
                           element={<ScheduledCallRoomPage />}
                        />
                        {/* <Route path='/communications' element={<ChatPage />} /> */}
                        <Route path='/ai-photo-studio' element={<AIPhotoStudioPage />} />
                        <Route path='/admin' element={<SuperAdminPage />} />
                        <Route path='/admin/event-management' element={<EventManagement />} />
                        <Route path='/organizations' element={<OrganizationsList />} />
                        <Route path='/organizations/create' element={<CreateOrganizationPage />} />
                        <Route path='/organizations/:id' element={<OrganizationProfile />} />
                        <Route path='/organizations/:id/:subId' element={<OrganizationProfile />} />
                        <Route
                           path='/organizations/:id/:subId/profile'
                           element={<OrganizationProfile />}
                        />
                        <Route
                           path='/organizations/:id/profile'
                           element={<OrganizationProfile />}
                        />
                        <Route
                           path='/organizations/:id/dashboard'
                           element={<OrganizationDashboard />}
                        />
                        <Route
                           path='/organizations/:id/timeline'
                           element={<OrganizationTimelineView />}
                        />
                        <Route
                           path='/organizations/:id/calendar'
                           element={<OrganizationCalendar />}
                        />
                        <Route path='/organizations/:id/map' element={<OrganizationMapView />} />
                        <Route
                           path='/organizations/:id/edit'
                           element={<CreateOrganizationPage />}
                        />
                        <Route path='/organizations/:id/events' element={<OrganizationEvents />} />
                        <Route
                           path='/organizations/:id/events/create'
                           element={<CreateOrgEvent />}
                        />
                        <Route
                           path='/organizations/:id/events/:eventId'
                           element={<OrganizationEventDetail />}
                        />
                        <Route
                           path='/organizations/:id/events/:eventId/edit'
                           element={<EditOrgEvent />}
                        />
                        <Route
                           path='/organizations/:id/trips'
                           element={<OrganizationTripsPage />}
                        />
                        <Route
                           path='/organizations/:id/trips/:tripId'
                           element={<TripDetailPage />}
                        />
                        <Route
                           path='/organizations/:id/people'
                           element={<OrganizationPeoplePage />}
                        />
                        <Route
                           path='/organizations/:id/attendees'
                           element={<OrganizationAttendees />}
                        />
                        <Route path='/organizations/:id/chat' element={<OrganizationChat />} />
                        <Route
                           path='/organizations/:id/subscriptions'
                           element={<OrganizationSubscriptions />}
                        />
                        <Route
                           path='/organizations/:id/upload'
                           element={<OrganizationUploadPhotos />}
                        />
                        <Route
                           path='/organizations/:id/contacts'
                           element={<OrganizationContacts />}
                        />
                        <Route
                           path='/organizations/:id/contacts/create'
                           element={<ContactCreate />}
                        />
                        <Route
                           path='/organizations/:id/contacts/:contactId'
                           element={<ContactDetail />}
                        />
                        <Route
                           path='/organizations/:id/contacts/:contactId/edit'
                           element={<ContactDetail />}
                        />
                        <Route
                           path='/organizations/:id/marketing-studio'
                           element={<OrganizationMarketingStudio />}
                        />
                        <Route path='/organizations/:id/asset-gallery' element={<AssetGallery />} />
                        <Route
                           path='/organizations/:id/asset-gallery/:category'
                           element={<AssetGallery />}
                        />
                        <Route
                           path='/organizations/:id/asset-gallery/:category/:assetId'
                           element={<AssetGallery />}
                        />
                        <Route
                           path='/organizations/:id/asset-gallery/:assetId'
                           element={<AssetGallery />}
                        />
                        <Route
                           path='/organizations/:id/photo/:photoId'
                           element={<PhotoDetails />}
                        />
                        <Route
                           path='/organizations/:id/admin-dashboard'
                           element={<OrganizationAdminDashboard />}
                        />
                        {/* Venue Routes */}
                        <Route path='/venues' element={<VenuesList />} />
                        <Route path='/venues/create' element={<CreateVenuePage />} />
                        <Route path='/venues/:venueId' element={<VenueDetail />} />
                        <Route path='/venues/:venueId/dashboard' element={<VenueDashboard />} />
                        <Route
                           path='/venues/:venueId/packages/create'
                           element={<CreatePackage />}
                        />
                        <Route
                           path='/venues/:venueId/packages/:packageId'
                           element={<PackageDetail />}
                        />
                        <Route
                           path='/venues/:venueId/packages/:packageId/edit'
                           element={<EditPackage />}
                        />
                        <Route path='/venues/:venueId/bookings' element={<VenueBookingsPage />} />
                        <Route path='/venues/bookings' element={<VenueBookingsPage />} />
                        {/* Public Package Routes */}
                        <Route path='/explore/packages' element={<ExplorePackages />} />
                        <Route
                           path='/explore/packages/:venueId/:packageId'
                           element={<PublicPackageDetail />}
                        />
                        <Route
                           path='/organizations/:id/events/:eventId/assets'
                           element={<AssetGallery />}
                        />
                        <Route
                           path='/organizations/:id/trips/:tripId/assets'
                           element={<AssetGallery />}
                        />
                        <Route
                           path='/organizations/:id/subscriptions/:membershipId/assets'
                           element={<AssetGallery />}
                        />
                        <Route path='/subscriber' element={<SubscriberPage />} />
                        <Route
                           path='/subscription-purchase/:organizationId/:planId'
                           element={<SubscriptionPurchasePage />}
                        />
                        <Route
                           path='/subscribe/:subscriptionId'
                           element={<SubscriptionPurchasePage />}
                        />
                        <Route
                           path='/explore-subscriptions'
                           element={<ExploreSubscriptionsPage />}
                        />
                        <Route path='/my-tickets' element={<MyTicketsPage />} />
                        <Route path='/my-bookings' element={<MyBookingsPage />} />
                        <Route path='/bookings/:bookingId' element={<BookingDetailPage />} />
                        <Route path='/explore-events' element={<ExploreEventsPage />} />
                        <Route
                           path='/event-details/:organizationId/:eventId'
                           element={<EventDetailsPage />}
                        />
                        <Route
                           path='/ticket-purchase/:organizationId/:eventId'
                           element={<TicketPurchasePage />}
                        />
                        <Route path='/albums' element={<AlbumsListPage />} />
                        <Route path='/albums/:albumId' element={<AlbumDetailPage />} />
                        <Route path='/personal-photos' element={<PersonalPhotos />} />
                        <Route path='/shared-collections' element={<PersonalPhotos />} />
                        <Route path='/received-collections' element={<PersonalPhotos />} />
                        <Route path='/download-analytics' element={<PersonalPhotos />} />
                     </Route>

                     {/* Redirect unmatched routes to landing page */}
                     <Route path='*' element={<Navigate to='/' replace />} />
                  </Routes>
               </ScheduledCallVideoProvider>
            </VideoCallProvider>
         </Router>
      </MarketingStudioProvider>
   );
};

// Add this function to check the Stability API key on startup
const checkStabilityAPIKey = async (): Promise<boolean> => {
   const API_KEY: string | undefined = process.env.REACT_APP_STABILITY_AI_API_KEY;

   if (!API_KEY) {
      console.error('⚠️ STABILITY AI API KEY NOT FOUND - AI image features will use mock mode');
      return false;
   }

   try {
      // Make a simple request to check if the key is valid
      const response: Response = await fetch('https://api.stability.ai/v1/user/account', {
         method: 'GET',
         headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
         },
      });

      if (response.ok) {
         const data: any = await response.json();
         console.info('✅ Stability AI API Key valid - Credits available:', data.credits);
         return true;
      } else {
         console.error('❌ Stability AI API Key invalid or expired:', response.status);
         return false;
      }
   } catch (error: any) {
      console.error('❌ Failed to connect to Stability AI:', error);
      return false;
   }
};

// Root App component that provides context
function App() {
   useEffect(() => {
      checkStabilityAPIKey();
   }, []);

   return (
      <ThemeProvider theme={theme}>
         <CssBaseline />
         <DndProvider backend={HTML5Backend}>
            <ResponsiveProvider>
               <Provider store={store}>
                  <PersistGate loading={null} persistor={persistor}>
                     <UserProvider>
                        <SubscriptionProvider>
                           <AuthProvider>
                              <TripProvider>
                                 <EventProvider>
                                    <OrganizationProvider>
                                       <VenueProvider>
                                          <MarketingStudioProvider>
                                             <WebSocketChatProvider>
                                                <FloatingChatProvider>
                                                   <AppContent />
                                                </FloatingChatProvider>
                                             </WebSocketChatProvider>
                                          </MarketingStudioProvider>
                                       </VenueProvider>
                                    </OrganizationProvider>
                                 </EventProvider>
                              </TripProvider>
                           </AuthProvider>
                        </SubscriptionProvider>
                     </UserProvider>
                  </PersistGate>
               </Provider>
            </ResponsiveProvider>
         </DndProvider>
      </ThemeProvider>
   );
}

export default App;
